import { useCallback, useRef } from "react";

import { parseGid } from "@shopify/hydrogen-react";
import { type FilterValue } from "@shopify/hydrogen-react/storefront-api-types";

import { prioritizeColorOptions } from "@react/utilities/collection";
import {
  COLLECTION_FILTER_IDS,
  COLLECTION_PARAMS,
} from "@sections/Collection/CollectionFilters/constants";
import { useSectionSettings } from "@sections/Root/SectionSettingsContext";
import {
  type CollectionSectionSettings,
  type PriceRange,
} from "@tablescape/state";

// Event enums
export enum CollectionAnalyticsEvents {
  CLICK_FILTER = "clickFilter",
  CLICK_SORT_BY = "clickSortBy",
  VIEW_COLLECTION_GRID = "viewCollectionGrid",
  VIEW_PAGE = "viewPage",
}

// Type definitions for event data
export interface FilterEventData {
  action: "apply" | "remove" | "clear all";
  "filter type": string;
  "filter value": string;
  "from path": string;
  "from url": string;
  "previous filter value": string[];
}

export interface SortEventData {
  "from path": string;
  "from url": string;
  "sort value": string;
}

export interface PageViewEventData {
  market: string;
  path: string;
  url: string;
}

interface CollectionGridImpressionData {
  "filter value": string;
  path: string;
  positions: string[];
  product_ids: string[];
  product_names: string[];
  "sort value": string;
  variant_ids: string[];
  variant_names: string[];
}

// Click deduplication state
interface ClickState {
  lastEventType: string | null;
  lastTarget: string | null;
  lastTimestamp: number;
}

// Helper functions
const getCurrentUrl = (): string => {
  if (typeof window === "undefined") return "";
  return window.location.href;
};

const getCurrentPath = (): string => {
  if (typeof window === "undefined") return "";
  return window.location.pathname;
};

const generateClickId = (eventType: string, target: string): string => {
  return `${eventType}-${target}`;
};

const shouldFireEvent = (
  clickState: ClickState,
  eventType: string,
  target: string,
  minInterval: number = 100
): boolean => {
  const now = Date.now();
  const clickId = generateClickId(eventType, target);
  const lastClickId = generateClickId(
    clickState.lastEventType || "",
    clickState.lastTarget || ""
  );

  // Fire if it's a different event type, different target, or enough time has passed
  return (
    clickId !== lastClickId || now - clickState.lastTimestamp >= minInterval
  );
};

const updateClickState = (
  clickState: ClickState,
  eventType: string,
  target: string
): void => {
  clickState.lastEventType = eventType;
  clickState.lastTarget = target;
  clickState.lastTimestamp = Date.now();
};

export type CollectionGridImpressionProduct = {
  id: string;
  options?: Array<{
    name: string;
    optionValues?: Array<{ name: string }>;
  }>;
  title: string;
  variants: {
    nodes: Array<{
      collectionNames?: { value: string } | null;
      colorGroup?: { value: string } | null;
      hidden?: { value: string } | null;
      id: string;
      selectedOptions?: Array<{ name: string; value: string }>;
      title?: string;
    }>;
  };
};

type ImpressionColorFilter = {
  id: string;
  input?: unknown;
  label: string;
};

function parseStringList(value: string | null | undefined): string[] | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      Array.isArray(parsed) &&
      parsed.every((item): item is string => typeof item === "string")
    ) {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
}

function colorFiltersForPriority(
  filters: ImpressionColorFilter[]
): FilterValue[] {
  return filters.flatMap((filter) =>
    typeof filter.input === "string"
      ? [
          {
            count: 0,
            id: filter.id,
            input: filter.input,
            label: filter.label,
          },
        ]
      : []
  );
}

function untouchedCardColorName(
  product: CollectionGridImpressionProduct,
  appliedColorFilters: ImpressionColorFilter[],
  variantMatchCollectionName: string | undefined,
  visibleVariants: CollectionGridImpressionProduct["variants"]["nodes"]
): string | undefined {
  const colorOption = product.options?.find(
    (option) => option.name === "Color" || option.name === "Colour"
  );
  const optionValues = colorOption?.optionValues;
  if (!colorOption || !optionValues?.length) return undefined;

  if (appliedColorFilters.length > 0) {
    const eligibleOptionValues = variantMatchCollectionName
      ? optionValues.filter((optionValue) =>
          visibleVariants.some(
            (variant) =>
              parseStringList(variant.collectionNames?.value)?.includes(
                variantMatchCollectionName
              ) &&
              variant.selectedOptions?.some(
                (option) =>
                  option.name === colorOption.name &&
                  option.value === optionValue.name
              )
          )
        )
      : optionValues;
    const prioritized = prioritizeColorOptions(
      eligibleOptionValues,
      colorFiltersForPriority(appliedColorFilters),
      visibleVariants.map((variant) => ({
        colorGroup: parseStringList(variant.colorGroup?.value),
        selectedOptions: variant.selectedOptions ?? [],
      }))
    );
    const prioritizedName = prioritized[0]?.name;
    if (prioritizedName && prioritizedName !== optionValues[0]?.name) {
      return prioritizedName;
    }
    return undefined;
  }

  if (!variantMatchCollectionName) return undefined;

  const namesInCollection = new Set(
    visibleVariants
      .filter((variant) =>
        parseStringList(variant.collectionNames?.value)?.includes(
          variantMatchCollectionName
        )
      )
      .map(
        (variant) =>
          variant.selectedOptions?.find(
            (option) => option.name === colorOption.name
          )?.value
      )
      .filter((value): value is string => typeof value === "string")
  );

  return optionValues.find((optionValue) =>
    namesInCollection.has(optionValue.name)
  )?.name;
}

function getImpressionVariant(
  product: CollectionGridImpressionProduct,
  appliedColorFilters: ImpressionColorFilter[],
  variantMatchCollectionName: string | undefined
) {
  const visibleVariants = product.variants.nodes.filter(
    (variant) => variant.hidden?.value !== "true"
  );
  const cardColorName = untouchedCardColorName(
    product,
    appliedColorFilters,
    variantMatchCollectionName,
    visibleVariants
  );
  const variant =
    (cardColorName
      ? visibleVariants.find((visibleVariant) =>
          visibleVariant.selectedOptions?.some(
            (option) => option.value === cardColorName
          )
        )
      : undefined) ??
    visibleVariants.find((visibleVariant) =>
      visibleVariant.selectedOptions?.some((option) =>
        appliedColorFilters.some((filter) => filter.label === option.value)
      )
    ) ??
    visibleVariants[0];

  return {
    id: variant ? parseGid(variant.id).id : "",
    name: variant?.title ?? "",
  };
}

export function toCollectionDestPath(url: string): string {
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return url;
  }
  const parsed = new URL(url);
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function collectionGridClickData({
  destPath,
  position,
  productId,
  variantId,
  variantName,
}: {
  destPath: string;
  position: string;
  productId: string;
  variantId: string;
  variantName: string;
}) {
  return {
    "page section": "collection grid",
    position,
    product_id: parseGid(productId).id,
    variant_id: parseGid(variantId).id,
    variant_names: variantName,
    "dest path": destPath,
  };
}

// Main hook
export const useCollectionAnalytics = () => {
  const {
    meta: {
      localization: { country: market },
    },
  } = useSectionSettings<CollectionSectionSettings>();
  const clickStateRef = useRef<ClickState>({
    lastEventType: null,
    lastTarget: null,
    lastTimestamp: 0,
  });

  const fireEvent = useCallback(
    (
      eventType: string,
      data:
        | {
            action: "apply" | "remove" | "clear all";
            "filter type": string | null;
            "filter value": string | null;
            "from path": string;
            "from url": string;
            "previous filter value": string[];
          }
        | {
            "from path": string;
            "from url": string;
            "sort value": string;
          }
        | PageViewEventData
        | CollectionGridImpressionData
    ) => {
      if (typeof window !== "undefined" && window.maestro) {
        window.maestro.fireTrackEvent(eventType, {
          ...data,
          market, // Add market context to all events
        });
      }
    },
    [market]
  );

  // Helper method to get filter category from filter ID
  const getFilterCategory = useCallback((filterId: string) => {
    // Map filter IDs to their categories using COLLECTION_PARAMS enum
    if (filterId.startsWith(COLLECTION_FILTER_IDS.CAPACITY)) {
      return COLLECTION_PARAMS.CAPACITY;
    }

    if (filterId.startsWith(COLLECTION_FILTER_IDS.COLOR)) {
      return COLLECTION_PARAMS.COLOR;
    }

    if (filterId.startsWith(COLLECTION_FILTER_IDS.DIAMETER)) {
      return COLLECTION_PARAMS.DIAMETER;
    }

    if (filterId.startsWith(COLLECTION_FILTER_IDS.MATERIAL)) {
      return COLLECTION_PARAMS.MATERIAL;
    }

    if (filterId.startsWith(COLLECTION_FILTER_IDS.SIZE)) {
      return COLLECTION_PARAMS.SIZE;
    }

    if (filterId.startsWith(COLLECTION_FILTER_IDS.PRICE)) {
      return COLLECTION_PARAMS.PRICE;
    }

    // Fallback to original logic for unknown patterns
    return undefined;
  }, []);

  // Helper method to filter previous filter values by category
  const getPreviousFilterValues = useCallback(
    (appliedFilters: Array<{ id: string; label: string }>): string[] => {
      return appliedFilters.map((f) => f.label);
    },
    []
  );

  // Helper method to format price range for display
  const formatPriceRange = useCallback((priceRange: PriceRange): string => {
    return `$${priceRange.min} - $${priceRange.max || "∞"}`;
  }, []);

  const trackCollectionGridView = useCallback(
    ({
      appliedFilters,
      appliedSort,
      positions,
      products,
      variantMatchCollectionName,
    }: {
      appliedFilters: ImpressionColorFilter[];
      appliedSort: { name: string } | undefined;
      positions: string[];
      products: CollectionGridImpressionProduct[];
      variantMatchCollectionName?: string;
    }) => {
      const appliedColorFilters = appliedFilters.filter((filter) =>
        filter.id.startsWith(COLLECTION_FILTER_IDS.COLOR)
      );
      const variants = products.map((product) =>
        getImpressionVariant(
          product,
          appliedColorFilters,
          variantMatchCollectionName
        )
      );

      fireEvent(CollectionAnalyticsEvents.VIEW_COLLECTION_GRID, {
        positions: products.map((_, index) => positions[index] ?? ""),
        product_ids: products.map((product) => parseGid(product.id).id),
        product_names: products.map((product) => product.title),
        variant_ids: variants.map((variant) => variant.id),
        variant_names: variants.map((variant) => variant.name),
        path: getCurrentPath(),
        "sort value": appliedSort?.name ?? "",
        "filter value": appliedFilters.map((filter) => filter.label).join(", "),
      });
    },
    [fireEvent]
  );

  const getReporters = useCallback(() => {
    const clickState = clickStateRef.current;

    return {
      trackCollectionGridView,
      // Filter toggle tracking (apply/remove from facet UI)
      trackFilterToggle: (
        isSelected: boolean,
        filterType: string,
        filterValue: string,
        previousFilterValues: string[]
      ) => {
        const target = `filter-${filterType}-${filterValue}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          )
        ) {
          fireEvent(CollectionAnalyticsEvents.CLICK_FILTER, {
            action: isSelected ? "apply" : "remove",
            "filter type": filterType,
            "filter value": filterValue,
            "previous filter value": previousFilterValues,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          );
        }
      },

      // Filter deselection tracking (from Applied Filters pills)
      trackFilterDeselection: (
        filterType: string,
        filterValue: string,
        previousFilterValues: string[]
      ) => {
        const target = `filter-pill-${filterType}-${filterValue}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          )
        ) {
          fireEvent(CollectionAnalyticsEvents.CLICK_FILTER, {
            action: "remove",
            "filter type": filterType,
            "filter value": filterValue,
            "previous filter value": previousFilterValues,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          );
        }
      },

      // Clear all filters tracking
      trackClearAllFilters: (
        clearedFilters: Array<{ type: string; value: string }>,
        previousFilterValues: string[]
      ) => {
        const target = "clear-all-filters";

        // Only fire event if there are actually filters to clear
        if (
          clearedFilters.length > 0 &&
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          )
        ) {
          // Fire a single clear all event instead of individual events
          fireEvent(CollectionAnalyticsEvents.CLICK_FILTER, {
            action: "clear all",
            "filter type": null,
            "filter value": null,
            "previous filter value": previousFilterValues,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          );
        }
      },

      // Price filter change tracking
      trackPriceFilterChange: (
        priceRange: PriceRange,
        previousFilterValues: string[]
      ) => {
        const target = `price-filter-${priceRange.min}-${priceRange.max}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          )
        ) {
          const filterValue = formatPriceRange(priceRange);

          fireEvent(CollectionAnalyticsEvents.CLICK_FILTER, {
            action: "apply",
            "filter type": "Price",
            "filter value": filterValue,
            "previous filter value": previousFilterValues,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          );
        }
      },

      // Price filter removal tracking
      trackPriceFilterRemoval: (
        priceRange: PriceRange,
        previousFilterValues: string[]
      ) => {
        const target = `price-filter-remove-${priceRange.min}-${priceRange.max}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          )
        ) {
          const filterValue = formatPriceRange(priceRange);

          fireEvent(CollectionAnalyticsEvents.CLICK_FILTER, {
            action: "remove",
            "filter type": "Price",
            "filter value": filterValue,
            "previous filter value": previousFilterValues,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_FILTER,
            target
          );
        }
      },

      // Sort option change tracking
      trackSortChange: (sortValue: string) => {
        const target = `sort-${sortValue}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.CLICK_SORT_BY,
            target
          )
        ) {
          fireEvent(CollectionAnalyticsEvents.CLICK_SORT_BY, {
            "sort value": sortValue,
            "from url": getCurrentUrl(),
            "from path": getCurrentPath(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.CLICK_SORT_BY,
            target
          );
        }
      },

      // Page view tracking for collection navigation
      trackPageView: () => {
        const target = `page-view-${getCurrentPath()}`;

        if (
          shouldFireEvent(
            clickState,
            CollectionAnalyticsEvents.VIEW_PAGE,
            target
          )
        ) {
          fireEvent(CollectionAnalyticsEvents.VIEW_PAGE, {
            market,
            path: getCurrentPath(),
            url: getCurrentUrl(),
          });

          updateClickState(
            clickState,
            CollectionAnalyticsEvents.VIEW_PAGE,
            target
          );
        }
      },
    };
  }, [fireEvent, formatPriceRange, market, trackCollectionGridView]);

  return {
    market,
    ...getReporters(),
    // Expose helper methods for use in components
    getFilterCategory,
    getPreviousFilterValues,
    formatPriceRange,
  };
};
